"use strict";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asText(value, fallback = "") {
  const next = String(value || "").trim();
  return next || fallback;
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function sanitizeEmployerName(value) {
  const label = asText(value);
  if (!label) return "";
  if (/^[A-Z]{2}\d+[A-Z]$/i.test(label)) return "";
  if (/^legacy-employer-\d+$/i.test(label)) return "";
  if (/^[a-z0-9]+_emp_\d+$/i.test(label)) return "";
  return label;
}

function getOpenItems(messageWorkspace = {}) {
  const workspace = asObject(messageWorkspace);
  return asArray(workspace.items)
    .filter((item) => item && item.folder !== "archive" && item.task_status !== "done");
}

function normalizeActionKind(href = "", label = "") {
  const normalizedHref = asText(href).toLowerCase();
  const normalizedLabel = asText(label).toLowerCase();

  if (normalizedHref.includes("/dashboard/applicant/filing-requests")) {
    if (normalizedLabel.includes("antwort") || normalizedLabel.includes("rückmeldung") || normalizedLabel.includes("nachweis")) {
      return "respond_case";
    }
    return "open_case";
  }
  if (normalizedHref.includes("/dashboard/applicant/nachrichten")) return "open_messages";
  if (normalizedHref.includes("/dashboard/applicant/edit-profile")) return "complete_profile";
  if (normalizedHref.includes("/dashboard/applicant/bewerbung") && normalizedHref.includes("tab=cv")) return "complete_cv";
  if (normalizedHref.includes("/dashboard/applicant/bewerbung") && normalizedHref.includes("tab=skills")) return "complete_skills";
  if (normalizedHref.includes("/dashboard/applicant/sprachtest-b1")) {
    if (normalizedLabel.includes("freig")) return "review_release";
    if (normalizedLabel.includes("fort")) return "continue_language";
    return "start_language";
  }
  if (normalizedHref.includes("/dashboard/applicant/matches")) return "open_matches";
  if (normalizedHref.includes("/dashboard/applicant/erfolge")) return "review_progress";
  if (normalizedHref.includes("/dashboard/account")) return "review_account";
  return "open_area";
}

function normalizeBlockerKind(blocker = {}) {
  const href = asText(blocker.href).toLowerCase();
  const label = asText(blocker.label).toLowerCase();

  if (href.includes("/edit-profile")) return "profile_incomplete";
  if (href.includes("tab=cv")) return "cv_incomplete";
  if (href.includes("tab=skills")) return "skills_incomplete";
  if (href.includes("/sprachtest-b1") && (label.includes("sichtbar") || label.includes("freig") || label.includes("visible"))) {
    return "visibility_release_missing";
  }
  if (href.includes("/sprachtest-b1")) return "language_missing";
  return "general_blocker";
}

function normalizeDriverKind(driver = {}) {
  const href = asText(driver.href).toLowerCase();
  const label = asText(driver.label).toLowerCase();

  if (href.includes("/dashboard/applicant/matches") && label.includes("chance")) return "active_chances";
  if (href.includes("/dashboard/applicant/matches")) return "active_chances";
  if (href.includes("tab=skills")) return "skills_visible";
  if (href.includes("/dashboard/applicant/sprachtest-b1") && label.includes("sehen")) return "visible_signal";
  if (href.includes("/dashboard/applicant/sprachtest-b1") && label.includes("deutsch")) return "language_ready";
  if (href.includes("/dashboard/applicant/sprachtest-b1")) return "language_ready";
  return "steady_progress";
}

function buildCandidateSignature(candidate = {}) {
  return [
    asText(candidate.variant, "item"),
    asText(candidate.href, "none"),
    asText(candidate.title, "untitled"),
  ].join(":");
}

function deriveMessageVariant(item = {}) {
  if (item.source_type === "filing_request") return "open_requirement";
  if (item.due_state === "overdue" || item.due_state === "due_soon") return "open_deadline";
  return "message_review";
}

function deriveOpenKind(item = {}) {
  if (item.source_type === "filing_request" || item.source_type === "filing_notification" || item.source_type === "filing_response" || Number(item.case_id || 0) > 0) {
    return "case";
  }
  if (item.source_type === "opportunity") return "opportunity";
  if (asText(item.href).includes("/dashboard/applicant/nachrichten")) return "message";
  return "area";
}

function deriveMessageActionKind(item = {}) {
  if (item.source_type === "filing_request" && item.capabilities?.can_respond) return "respond_case";
  return normalizeActionKind(item.href, item.next_step_label || item.subject || "");
}

function toPriorityCandidate(item = {}) {
  return {
    key: asText(item.message_key, "message"),
    lane: "obligation",
    variant: deriveMessageVariant(item),
    title: asText(item.subject, "Applicant item"),
    href: asText(item.href, "/dashboard/applicant/nachrichten"),
    open_kind: deriveOpenKind(item),
    next_action_kind: deriveMessageActionKind(item),
    next_href: asText(item.href, "/dashboard/applicant/nachrichten"),
    due_at: item.due_at || null,
    due_state: asText(item.due_state, "none"),
    sender_name: asText(item.sender_name),
    sender_role: asText(item.sender_role),
    source_type: asText(item.source_type),
    preview: asText(item.preview),
    count: 1,
  };
}

function buildObligationCandidates({ messageWorkspace = {} }) {
  const workspace = asObject(messageWorkspace);
  const openItems = getOpenItems(workspace);
  const candidates = [];
  const urgentRequirement = openItems.find((item) => item.source_type === "filing_request" && item.due_state === "overdue")
    || openItems.find((item) => item.source_type === "filing_request" && item.due_state === "due_soon")
    || openItems.find((item) => item.source_type === "filing_request")
    || null;
  const urgentMessage = openItems.find((item) => item.due_state === "overdue")
    || openItems.find((item) => item.due_state === "due_soon")
    || null;
  const topItem = openItems[0] || null;

  [urgentRequirement, urgentMessage, topItem]
    .filter(Boolean)
    .forEach((item) => candidates.push(toPriorityCandidate(item)));

  const summary = asObject(workspace.summary);
  candidates.push({
    key: "obligation:clear",
    lane: "obligation",
    variant: "no_obligation",
    title: "Board aktuell ruhig",
    href: asText(summary.lead_href, "/dashboard/applicant/nachrichten"),
    open_kind: "area",
    next_action_kind: openItems.length ? "open_messages" : "open_area",
    next_href: asText(summary.lead_href, "/dashboard/applicant/nachrichten"),
    due_at: null,
    due_state: "none",
    count: Number(summary.open_count || summary.new_count || 0),
  });

  return candidates;
}

function buildBlockerCandidates({ snapshot = {} }) {
  const safeSnapshot = asObject(snapshot);
  const blockers = asArray(safeSnapshot.blockers).map((blocker, index) => ({
    key: asText(blocker.key, `blocker-${index}`),
    lane: "blocker",
    variant: normalizeBlockerKind(blocker),
    title: asText(blocker.label, "Sichtbarkeitsblocker"),
    href: asText(blocker.href, "/dashboard/applicant"),
    open_kind: "area",
    next_action_kind: normalizeActionKind(blocker.href, blocker.action),
    next_href: asText(blocker.href, "/dashboard/applicant"),
    due_at: null,
    due_state: "none",
    reason: asText(blocker.reason),
    action_label: asText(blocker.action),
  }));

  blockers.push({
    key: "blocker:clear",
    lane: "blocker",
    variant: "no_blocker",
    title: "Keine akuten Sichtbarkeitsblocker",
    href: asText(safeSnapshot?.actions?.primary?.href, "/dashboard/applicant"),
    open_kind: "area",
    next_action_kind: normalizeActionKind(safeSnapshot?.actions?.primary?.href, safeSnapshot?.actions?.primary?.label),
    next_href: asText(safeSnapshot?.actions?.primary?.href, "/dashboard/applicant"),
    due_at: null,
    due_state: "none",
    reason: "",
    action_label: asText(safeSnapshot?.actions?.primary?.label, "Weitergehen"),
  });

  return blockers;
}

function buildOpportunityCandidate(opportunity = {}) {
  return {
    key: `opportunity:${asText(opportunity.job_id, "featured")}`,
    lane: "accelerator",
    variant: "top_opportunity",
    title: asText(opportunity.title, "Passende Chance"),
    href: asText(opportunity.href || opportunity.next_step_href, "/dashboard/applicant/matches"),
    open_kind: "opportunity",
    next_action_kind: normalizeActionKind(opportunity.next_step_href || opportunity.href, opportunity.next_step_label),
    next_href: asText(opportunity.next_step_href || opportunity.href, "/dashboard/applicant/matches"),
    due_at: null,
    due_state: "none",
    employer_name: sanitizeEmployerName(opportunity.employer_name),
    region_label: asText(opportunity.region_label),
    fit_band: asText(opportunity.fit_band),
    next_label: asText(opportunity.next_step_label),
    why_relevant: asArray(opportunity.why_relevant).slice(0, 3),
  };
}

function buildDriverCandidate(driver = {}, index = 0) {
  return {
    key: asText(driver.key, `driver-${index}`),
    lane: "accelerator",
    variant: normalizeDriverKind(driver),
    title: asText(driver.label, "Nächster Fortschritt"),
    href: asText(driver.href, "/dashboard/applicant"),
    open_kind: "area",
    next_action_kind: normalizeActionKind(driver.href, driver.action),
    next_href: asText(driver.href, "/dashboard/applicant"),
    due_at: null,
    due_state: "none",
    reason: asText(driver.reason),
    action_label: asText(driver.action),
  };
}

function buildAcceleratorCandidates({ snapshot = {} }) {
  const safeSnapshot = asObject(snapshot);
  const candidates = [];
  const featuredOpportunity = asArray(safeSnapshot.opportunities)[0] || null;
  if (featuredOpportunity) {
    candidates.push(buildOpportunityCandidate(featuredOpportunity));
  }

  asArray(safeSnapshot.drivers).forEach((driver, index) => {
    candidates.push(buildDriverCandidate(driver, index));
  });

  if (safeSnapshot?.actions?.primary?.href) {
    candidates.push({
      key: "accelerator:primary-action",
      lane: "accelerator",
      variant: "next_action",
      title: asText(safeSnapshot.actions.primary.label, "Nächster Schritt"),
      href: asText(safeSnapshot.actions.primary.href, "/dashboard/applicant"),
      open_kind: "area",
      next_action_kind: normalizeActionKind(safeSnapshot.actions.primary.href, safeSnapshot.actions.primary.label),
      next_href: asText(safeSnapshot.actions.primary.href, "/dashboard/applicant"),
      due_at: null,
      due_state: "none",
      reason: asText(safeSnapshot.actions.primary.reason),
      action_label: asText(safeSnapshot.actions.primary.label),
    });
  }

  candidates.push({
    key: "accelerator:steady-progress",
    lane: "accelerator",
    variant: "steady_progress",
    title: "Stabiler Fortschritt",
    href: asText(safeSnapshot?.actions?.primary?.href, "/dashboard/applicant"),
    open_kind: "area",
    next_action_kind: normalizeActionKind(safeSnapshot?.actions?.primary?.href, safeSnapshot?.actions?.primary?.label),
    next_href: asText(safeSnapshot?.actions?.primary?.href, "/dashboard/applicant"),
    due_at: null,
    due_state: "none",
    reason: "",
    action_label: asText(safeSnapshot?.actions?.primary?.label, "Weitergehen"),
  });

  return candidates;
}

function pickUniqueCandidate(candidates = [], usedSignatures = new Set()) {
  for (const candidate of candidates) {
    const signature = buildCandidateSignature(candidate);
    if (usedSignatures.has(signature)) continue;
    usedSignatures.add(signature);
    return candidate;
  }
  return null;
}

export function buildApplicantDailyBriefing({ snapshot = null, messageWorkspace = null }) {
  const usedSignatures = new Set();
  const obligation = pickUniqueCandidate(buildObligationCandidates({ messageWorkspace }), usedSignatures);
  const blocker = pickUniqueCandidate(buildBlockerCandidates({ snapshot }), usedSignatures);
  const accelerator = pickUniqueCandidate(buildAcceleratorCandidates({ snapshot }), usedSignatures);
  const items = [obligation, blocker, accelerator].filter(Boolean).map((item, index) => ({
    ...item,
    priority_rank: index + 1,
  }));

  const openCount = Number(messageWorkspace?.summary?.open_count || 0);
  const overdueCount = Number(messageWorkspace?.summary?.overdue_count || 0);
  const blockerCount = asArray(snapshot?.blockers).length;
  const hasOpportunity = Boolean(asArray(snapshot?.opportunities)[0]);
  const emphasis = overdueCount > 0 || obligation?.due_state === "overdue"
    ? "urgent"
    : blockerCount > 0
      ? "attention"
      : hasOpportunity
        ? "opportunity"
        : "steady";

  return {
    items,
    emphasis,
    source_ready: Boolean(snapshot || messageWorkspace),
    has_open_items: openCount > 0,
    has_overdue_items: overdueCount > 0,
    blocker_count: blockerCount,
    open_count: openCount,
    opportunity_count: asArray(snapshot?.opportunities).length,
  };
}

function buildCoachMessageLabel(candidate = {}) {
  return asText(candidate.title, "Applicant Punkt");
}

function buildCoachMessageReason(candidate = {}) {
  if (candidate.variant === "open_requirement") {
    return candidate.due_state === "overdue"
      ? "Eine offene Anforderung ist bereits ueberfaellig."
      : candidate.due_state === "due_soon"
        ? "Eine offene Anforderung braucht bald deine Reaktion."
        : "Hier liegt gerade eine offene Anforderung im Messaging Board.";
  }
  if (candidate.variant === "open_deadline") {
    return candidate.due_state === "overdue"
      ? "Eine sichtbare Frist ist bereits abgelaufen."
      : "Eine sichtbare Frist liegt bald an.";
  }
  if (candidate.variant === "message_review") {
    return "Im Messaging Board liegt ein offener sichtbarer Punkt vorne.";
  }
  if (candidate.variant === "no_obligation") {
    return "Im Messaging Board liegt gerade keine harte Frist obenauf.";
  }
  if (candidate.variant === "profile_incomplete") return "Dein Profil blockiert aktuell noch Sichtbarkeit.";
  if (candidate.variant === "cv_incomplete") return "Dein CV wirkt aktuell noch nicht komplett genug.";
  if (candidate.variant === "skills_incomplete") return "Fehlende Skills bremsen deine Sichtbarkeit.";
  if (candidate.variant === "language_missing") return "Ohne Deutschsignal fehlt dir ein zentraler Sichtbarkeitshebel.";
  if (candidate.variant === "visibility_release_missing") return "Dein Sprachsignal ist noch nicht fuer Firmen freigegeben.";
  if (candidate.variant === "no_blocker") return "Gerade ist kein akuter Sichtbarkeitsblocker vorne sichtbar.";
  if (candidate.variant === "top_opportunity") return "Diese Chance ist aktuell dein staerkster sichtbarer Beschleuniger.";
  if (candidate.variant === "active_chances") return "Aktive Chancen koennen deinen Fortschritt direkt beschleunigen.";
  if (candidate.variant === "visible_signal") return "Dein sichtbares Signal hilft bereits bei Chancen und Vergleich.";
  if (candidate.variant === "language_ready") return "Dein vorhandenes Sprachsignal ist bereits ein Fortschrittstreiber.";
  if (candidate.variant === "skills_visible") return "Sichtbare Skills schaerfen dein Profil fuer passende Chancen.";
  if (candidate.variant === "next_action") return "Das ist aktuell dein naechster sinnvoller Schritt.";
  return "Hier liegt heute ein sichtbarer Fortschrittshebel.";
}

export function buildApplicantDashboardCoachSurface({ snapshot = null, briefing = null }) {
  const safeSnapshot = asObject(snapshot);
  const items = asArray(briefing?.items);
  const prioritized = items[0] || null;
  const primaryHref = asText(safeSnapshot?.actions?.primary?.href, "/dashboard/applicant");
  const primaryLabel = asText(safeSnapshot?.actions?.primary?.label, "Dashboard öffnen");
  const primaryReason = asText(safeSnapshot?.actions?.primary?.reason, "Hier liegt dein nächster sinnvoller Schritt.");
  const visibleMessages = items.map((item) => ({
    key: item.key,
    title: buildCoachMessageLabel(item),
    text: buildCoachMessageReason(item),
    href: asText(item.next_href || item.href, primaryHref),
    sender_name: item.lane === "obligation" ? "Messaging Board" : "Applicant Dashboard",
    sender_role: item.lane === "accelerator" ? "Chance" : "Applicant",
    next_step_label: asText(item.action_label, primaryLabel),
    due_label: item.due_at ? `Frist ${item.due_at}` : "",
    due_relative: item.due_state === "overdue" ? "überfällig" : item.due_state === "due_soon" ? "bald fällig" : "",
    status_labels: [item.lane, item.variant].filter(Boolean),
  }));

  return {
    screen_key: "applicant_dashboard",
    screen_label: "Applicant Dashboard",
    highlighted_step: prioritized
      ? `${buildCoachMessageLabel(prioritized)}: ${buildCoachMessageReason(prioritized)}`
      : primaryReason,
    surface_state_key: [
      "dashboard",
      briefing?.emphasis || "steady",
      items.map((item) => item.key).join("|") || "none",
      safeSnapshot?.mission?.score_total || "na",
    ].join(":"),
    surface_summary: {
      surface_type: "applicant_dashboard",
      status_label: items.length ? `${items.length} priorisierte Punkte` : "Kein Briefing",
      status_note: prioritized ? buildCoachMessageReason(prioritized) : primaryReason,
      prioritized_message: prioritized ? {
        key: prioritized.key,
        title: buildCoachMessageLabel(prioritized),
        text: buildCoachMessageReason(prioritized),
        href: asText(prioritized.next_href || prioritized.href, primaryHref),
        sender_name: prioritized.lane === "obligation" ? "Messaging Board" : "Applicant Dashboard",
        sender_role: prioritized.lane === "accelerator" ? "Chance" : "Applicant",
        next_step_label: asText(prioritized.action_label, primaryLabel),
        due_label: prioritized.due_at ? `Frist ${prioritized.due_at}` : "",
        due_relative: prioritized.due_state === "overdue" ? "überfällig" : prioritized.due_state === "due_soon" ? "bald fällig" : "",
        status_labels: [prioritized.lane, prioritized.variant].filter(Boolean),
      } : null,
      visible_messages: visibleMessages,
      coach_next_step: {
        key: asText(prioritized?.key, "dashboard"),
        label_de: buildCoachMessageLabel(prioritized) || primaryLabel,
        label_en: "open the top priority",
        reason_de: buildCoachMessageReason(prioritized) || primaryReason,
        href: asText(prioritized?.next_href || prioritized?.href, primaryHref),
      },
      coach_missing_items: items.map((item) => ({
        key: item.key,
        label_de: buildCoachMessageLabel(item),
        label_en: "briefing point",
        reason_de: buildCoachMessageReason(item),
        href: asText(item.next_href || item.href, primaryHref),
      })),
    },
  };
}

export function getApplicantBriefingDayKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
